/**
 * **The docked command menu's enter and leave motion** (`projects/token-command-menu.md`, the
 * 2026-09-24 dev-feedback unit). The menu follows the one selection, so it appears on select,
 * leaves on deselect, and on a switch between units the old card leaves while the new one enters,
 * in the card stack's own vocabulary (`token-command-menu.css`): the leaving menu takes the
 * ancestor (`left`) role's transform and fade, the entering one comes in from the left (the hidden
 * `right` role's transform, mirrored) with the `active` role's overshoot easing. Reduced motion
 * snaps both.
 *
 * `unitId` keys the shown menu; `children` is that unit's menu (or nothing). When `unitId` changes,
 * the last rendered menu stays mounted for {@link MENU_LEAVE_MS} as an inert, pointer-transparent
 * leaving copy (the same React instance, so it leaves showing the card it was on), while the new
 * one mounts at once, so a switch never waits for the new unit's rows.
 *
 * **R3 (a double-click opens only the sheet).** The menu appears on the select press, so a token
 * under the docked menu would put the menu under the second click of a double-click. For
 * {@link MENU_ENTER_PASSTHROUGH_MS} after it appears, the entering menu lets the pointer through to
 * the board (the double-click window the old delayed open waited out), so that click still reaches
 * the token and expands its sheet (the unit panel, `UnitColumn.tsx`, whose new card passes the
 * pointer through the same way).
 */
import { useEffect, useState, type ReactNode } from "react";
import "./token-command-menu.css";

/** The leaving copy's lifetime: the `left` role's exit (`transform 200ms ease-in`). */
export const MENU_LEAVE_MS = 200;
/** The entering menu is pointer-transparent this long: past the browser's double-click window (R3). */
export const MENU_ENTER_PASSTHROUGH_MS = 300;

export function CommandMenuPresence({
  unitId,
  children,
  leaveMs = MENU_LEAVE_MS,
  enterMs = MENU_ENTER_PASSTHROUGH_MS,
}: {
  unitId: string | null;
  children: ReactNode;
  /** Test seams; the defaults are the design values. */
  leaveMs?: number;
  enterMs?: number;
}) {
  const [leaving, setLeaving] = useState<{ unitId: string; node: ReactNode }[]>([]);
  const [entering, setEntering] = useState(unitId !== null);
  // The shown unit and its newest render, as of the last render: what a switch leaves with.
  const [shown, setShown] = useState<{ unitId: string | null; node: ReactNode }>({ unitId, node: children });
  if (shown.unitId !== unitId) {
    // A switch, handled during render (React's "adjust state on a prop change"), so the leaving copy
    // is in the very render that drops it as the shown one and keeps its instance. A unit shown
    // again while still leaving is shown, not left.
    const gone = shown.unitId;
    setLeaving([...leaving.filter((x) => x.unitId !== unitId && x.unitId !== gone), ...(gone !== null ? [{ unitId: gone, node: shown.node }] : [])]);
    setShown({ unitId, node: children });
    setEntering(unitId !== null);
  } else if (shown.node !== children) {
    setShown({ unitId, node: children });
  }
  // Lifetimes: each leaving copy goes after `leaveMs`; the entering window closes after `enterMs`.
  useEffect(() => {
    if (leaving.length === 0) return;
    const t = setTimeout(() => setLeaving((l) => l.slice(1)), leaveMs);
    return () => clearTimeout(t);
  }, [leaving, leaveMs]);
  useEffect(() => {
    if (!entering) return;
    const t = setTimeout(() => setEntering(false), enterMs);
    return () => clearTimeout(t);
  }, [entering, unitId, enterMs]);

  // One keyed list, so a menu moving from shown to leaving keeps its instance.
  const items = [
    ...leaving.filter((l) => l.unitId !== unitId).map((l) => ({ unitId: l.unitId, node: l.node, cls: "is-leaving" })),
    ...(unitId !== null ? [{ unitId, node: children, cls: entering ? "is-entering" : "is-shown" }] : []),
  ];
  return (
    <>
      {items.map((it) => (
        <div
          key={it.unitId}
          className={`tcm-presence ${it.cls}`}
          data-unit-id={it.unitId}
          {...(it.cls === "is-leaving" ? { "aria-hidden": true, inert: true } : {})}
        >
          {it.node}
        </div>
      ))}
    </>
  );
}
